// Menu data layer. The live menu comes from /api/menu (DB-driven via the
// admin UI; the worker only returns available = 1, so 86'd products vanish
// from every surface automatically). Use useMenu() in components — the
// static arrays below are the demo catalog, kept as the type reference and
// dev fixture only.
import { useEffect, useState } from "react";

export type Category = { id: string; slug: string; name: string; emoji: string };
export type Product = {
  sku: string;
  category_id: string;
  name: string;
  description: string;
  price: number;        // MXN
  image: string;        // emoji or URL placeholder
  available: boolean;
  name_i18n?: Record<string, string> | null;
  description_i18n?: Record<string, string> | null;
  allergens?: string[];
};

export const ALLERGEN_ICON: Record<string, string> = {
  alcohol: "🍷", vegan: "🌱", vegetarian: "🥗", gluten_free: "🌾",
  nuts: "🥜", dairy: "🥛", spicy: "🌶️", shellfish: "🦐",
};

// Localized helpers — use when the kiosk has a selected language.
export function localizedName(p: Product, lang?: string | null): string {
  if (!lang) return p.name;
  return p.name_i18n?.[lang] || p.name;
}
export function localizedDescription(p: Product, lang?: string | null): string {
  if (!lang) return p.description;
  return p.description_i18n?.[lang] || p.description;
}

export const CATEGORIES: Category[] = [
  { id: "cocteles",     slug: "cocteles",     name: "Cócteles",            emoji: "🍸" },
  { id: "cervezas",     slug: "cervezas",     name: "Cervezas",            emoji: "🍺" },
  { id: "destilados",   slug: "destilados",   name: "Mezcal & Tequila",    emoji: "🥃" },
  { id: "snacks",       slug: "snacks",       name: "Snacks",              emoji: "🌮" },
];

export const PRODUCTS: Product[] = [
  // Cócteles
  { sku: "COC-001", category_id: "cocteles", name: "Margarita Clásica",   description: "Tequila, triple sec, limón, sal de Colima",     price: 145, image: "🍹", available: true },
  { sku: "COC-002", category_id: "cocteles", name: "Mezcalita de Mango",  description: "Mezcal joven, mango, chile piquín",              price: 165, image: "🍹", available: true },
  { sku: "COC-003", category_id: "cocteles", name: "Paloma Norteña",      description: "Tequila, toronja Squirt, sal Tajín",             price: 135, image: "🍹", available: true },
  { sku: "COC-004", category_id: "cocteles", name: "Mojito de la Casa",   description: "Ron, hierbabuena fresca, limón, soda",           price: 140, image: "🍹", available: true },
  { sku: "COC-005", category_id: "cocteles", name: "Carajillo",           description: "Licor 43 + espresso doble",                      price: 130, image: "🥃", available: true },

  // Cervezas
  { sku: "CER-001", category_id: "cervezas", name: "Corona",              description: "355ml — bien fría",                              price: 65,  image: "🍺", available: true },
  { sku: "CER-002", category_id: "cervezas", name: "Modelo Especial",     description: "355ml — la del oro",                             price: 70,  image: "🍺", available: true },
  { sku: "CER-003", category_id: "cervezas", name: "Pacífico",            description: "355ml — del mar de Cortés",                      price: 65,  image: "🍺", available: true },
  { sku: "CER-004", category_id: "cervezas", name: "Negra Modelo",        description: "355ml — vienna lager mexicana",                  price: 75,  image: "🍺", available: true },
  { sku: "CER-005", category_id: "cervezas", name: "IPA Local Cucapá",    description: "355ml — lúpulo de Baja",                          price: 95,  image: "🍺", available: true },

  // Destilados
  { sku: "DES-001", category_id: "destilados", name: "Caballito Don Julio Blanco",     description: "1oz — tequila premium",     price: 120, image: "🥃", available: true },
  { sku: "DES-002", category_id: "destilados", name: "Caballito Mezcal Espadín",       description: "1oz — Oaxaca, ahumado",     price: 130, image: "🥃", available: true },
  { sku: "DES-003", category_id: "destilados", name: "Caballito Mezcal Tobalá",        description: "1oz — silvestre, complejo", price: 220, image: "🥃", available: true },
  { sku: "DES-004", category_id: "destilados", name: "Caballito Tequila Reposado",     description: "1oz — añejado en roble",    price: 140, image: "🥃", available: true },
  { sku: "DES-005", category_id: "destilados", name: "Sotol Hacienda de Chihuahua",    description: "1oz — del norte profundo",  price: 160, image: "🥃", available: true },

  // Snacks
  { sku: "SNK-001", category_id: "snacks", name: "Guacamole + Totopos",     description: "Aguacate del día, lima, totopos azules", price: 110, image: "🥑", available: true },
  { sku: "SNK-002", category_id: "snacks", name: "Tacos al Pastor (3pz)",   description: "Maíz nixtamalizado, piña, cilantro",     price: 135, image: "🌮", available: true },
  { sku: "SNK-003", category_id: "snacks", name: "Esquites con Queso",      description: "Elote, mayo, cotija, chile",             price: 80,  image: "🌽", available: true },
  { sku: "SNK-004", category_id: "snacks", name: "Cacahuates Garapiñados",  description: "Dulces, picosos, adictivos",             price: 50,  image: "🥜", available: true },
  { sku: "SNK-005", category_id: "snacks", name: "Chicharrón Casero",       description: "Crujiente, con salsa verde",             price: 90,  image: "🍿", available: true },
];

export const productsByCategory = (catId: string) =>
  PRODUCTS.filter((p) => p.category_id === catId && p.available);

export const findProduct = (sku: string) => PRODUCTS.find((p) => p.sku === sku);

// ---------------------------------------------------------------------------
// Live menu from the API
// ---------------------------------------------------------------------------
export type MenuData = { categories: Category[]; products: Product[] };

export function mapApiMenu(j: { categories: any[]; products: any[] }): MenuData {
  return {
    categories: (j.categories ?? []).map((c: any) => ({
      id: c.id, slug: c.slug, name: c.name, emoji: c.emoji ?? "🍹",
    })),
    products: (j.products ?? []).map((p: any) => ({
      sku: p.sku,
      category_id: p.category_id,
      name: p.name,
      description: p.description ?? "",
      price: (p.price_mxn ?? 0) / 100,   // worker speaks centavos; kiosk displays pesos
      image: p.image || "🍹",
      available: !!p.available,
      name_i18n: p.name_i18n ?? null,
      description_i18n: p.description_i18n ?? null,
      allergens: p.allergens ?? [],
    })),
  };
}

let menuCache: MenuData | null = null;

export function useMenu(): MenuData & { loaded: boolean; findProduct: (sku: string) => Product | undefined } {
  const [data, setData] = useState<MenuData | null>(menuCache);
  useEffect(() => {
    let alive = true;
    fetch("/api/menu")
      .then((r) => r.json())
      .then((j: any) => { menuCache = mapApiMenu(j); if (alive) setData(menuCache); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const categories = data?.categories ?? [];
  const products = data?.products ?? [];
  return {
    categories,
    products,
    loaded: !!data,
    findProduct: (sku: string) => products.find((p) => p.sku === sku),
  };
}
